import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { fetchReleaseInfo, type ReleaseInfo } from '@/shared/utils/releaseMarkdown';

const STORAGE_KEY = 'dismissed-notifications';
const MAX_DISMISSED = 10;

interface NotificationContextValue {
  notification: ReleaseInfo | null;
  isDismissed: boolean;
  isDialogOpen: boolean;
  dismissNotification: () => void;
  openDialog: () => void;
  closeDialog: () => void;
}

const NotificationContext = createContext<NotificationContextValue>({
  notification: null,
  isDismissed: true,
  isDialogOpen: false,
  dismissNotification: () => {},
  openDialog: () => {},
  closeDialog: () => {},
});

function getDismissedIds(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function addDismissedId(id: string) {
  const ids = getDismissedIds();
  if (!ids.includes(id)) {
    ids.push(id);
  }
  // Keep only the last MAX_DISMISSED entries
  const trimmed = ids.slice(-MAX_DISMISSED);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
}

export function NotificationProvider({ children }: { children: ReactNode }) {
  const [notification, setNotification] = useState<ReleaseInfo | null>(null);
  const [isDismissed, setIsDismissed] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  useEffect(() => {
    fetchReleaseInfo().then((info) => {
      if (info) {
        setNotification(info);
        const dismissed = getDismissedIds().includes(info.version);
        setIsDismissed(dismissed);
      }
    });
  }, []);

  const dismissNotification = useCallback(() => {
    if (notification) {
      addDismissedId(notification.version);
    }
    setIsDismissed(true);
  }, [notification]);

  const openDialog = useCallback(() => setIsDialogOpen(true), []);
  const closeDialog = useCallback(() => setIsDialogOpen(false), []);

  return (
    <NotificationContext.Provider
      value={{
        notification,
        isDismissed,
        isDialogOpen,
        dismissNotification,
        openDialog,
        closeDialog,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotification() {
  return useContext(NotificationContext);
}
